import { Predicate } from '€lib/types/fp';

export type EntityID = string | number;

export type IndexedEntities<T> = { [id: string]: T };

export type DataSliceState<T> = {
    loading: boolean;
    error: string;
    allIds: EntityID[];
    byId: IndexedEntities<T> | {};
};

export type EntityState<T> = Pick<DataSliceState<T>, 'allIds' | 'byId'>;

export const entitiesToState =
    <T>(idProp: keyof T) =>
    (data: T[]): EntityState<T> =>
        data.reduce(
            (acc, curr: T) => {
                acc.allIds.push(String(curr[idProp]));

                acc.byId[String(curr[idProp])] = curr;

                return acc;
            },
            { allIds: [], byId: {} } as EntityState<T>,
        );

export const normalizedEntitiesToState = <T>(data: IndexedEntities<T>): EntityState<T> => ({
    byId: data,
    allIds: Object.keys(data || []),
});

export const mapEntitiesToIndexes =
    <T>(entities: IndexedEntities<T>) =>
    (indexes: EntityID[]): T[] =>
        indexes.reduce((acc, curr) => {
            const entity = entities[curr];

            if (entity) {
                acc.push(entity);
            }

            return acc;
        }, []);

export const mapEntitiesToIndexesBy =
    <T>(predicate: Predicate<T>) =>
    (entities: IndexedEntities<T>) =>
    (indexes: EntityID[]): T[] =>
        indexes.reduce((acc, curr) => {
            const entity = entities[curr];

            if (entity && predicate(entity)) {
                acc.push(entity);
            }

            return acc;
        }, []);

export const mergeEntities = <T>(originalEntities: EntityState<T>, newEntities: EntityState<T>) =>
    ({
        byId: { ...originalEntities.byId, ...newEntities.byId },
        allIds: originalEntities.allIds.concat(
            newEntities.allIds.filter(id => !originalEntities.allIds.includes(id)),
        ),
    } as EntityState<T>);
